import React, { useState } from "react"
import {
  WingsHeading,
  WingsText,
  WingsButton,
  WingsBadge,
  WingsDetailText,
  WingsIcon,
  WingsLink,
  WingsQuoteText,
} from "@wingspans-ui"
import { FormattedMessage, useIntl } from "react-intl"
import { CareerPlanColors } from "./careerPlanUtils"
import useMilestoneCategories from "./useMilestoneCategories"
import { Dropdown } from 'react-bootstrap'
import { resolveCssVariable, pickAccessibleTextColor } from "../../utils"
import ViewMilestoneModal from "./ViewMilestoneModal"
import { renderMonthName } from "../resume/utils/utils"
import "./CareerPlanPanel.css"

export const CareerPlanDisplayModes = {
  VIEW: "view",
  EDIT: "edit",
  TEMPLATE: "template",
}

const Milestone = ({ id, category, description, organization, enableEditing, color, completed, startDate="", endDate="", onEdit=()=>null, onToggleCompleted=()=>null, onDelete=()=>null}) => {
  const { locale } = useIntl()
  const { getCategoryLabel } = useMilestoneCategories()
  const categoryLabel = getCategoryLabel(category)
  const [modalOpen, setModalOpen] = useState(false)

  const checkColor = pickAccessibleTextColor(resolveCssVariable(color) || "#000000")

  const [startYear, startMonth] = startDate.split("-")
  const [endYear, endMonth] = endDate.split("-")
  const startYearNum = Number(startYear)
  const endYearNum = Number(endYear)
  const dateString = `${startYearNum || ""}${(startYearNum && endYearNum) ? "-" : ""}${endYearNum || ""}`

  const startMonthName = renderMonthName(startMonth, locale)
  const endMonthName = renderMonthName(endMonth, locale)
  const fullStartDate = `${startMonthName || ""}${(startMonthName && startYearNum) ? " " : ""}${startYearNum || ""}`
  const fullEndDate = `${endMonthName || ""}${(endMonthName && endYearNum) ? " " : ""}${endYearNum || ""}`
  const timePeriod = `${fullStartDate || ""}${(fullStartDate && fullEndDate) ? " - " : ""}${fullEndDate || ""}`

  return (
    <div className="career-plan-milestone-card-container w-shrink-0">
      <button
        className={`${completed ? "career-plan-milestone-completed" : "career-plan-milestone-uncompleted"} w-rounded w-py-1 w-pl-1 w-pr-2 w-flex w-flex-column w-items-start`}
        onClick={() => setModalOpen(!modalOpen)}
      >
        <WingsBadge className="career-plan-milestone-badge" backgroundColor={color}>
          {categoryLabel}
        </WingsBadge>
        {description && (
          <WingsText className="milestone-panel-description w-text-left">
            {description}
          </WingsText>
        )}
        {organization && (
          <WingsDetailText className="milestone-panel-organization w-text-left w-mt-1">
            {organization}
          </WingsDetailText>
        )}
        {dateString && (
          <WingsDetailText className="w-font-bold">
            {dateString}
          </WingsDetailText>
        )}
        {completed && (
          <WingsIcon
            className="milestone-completion-check"
            icon="check-circle"
            color={color}
            size="mini"
            style={{background: `radial-gradient(circle, ${checkColor} 0 60%, transparent 60% 100%)`}}
          />
        )}
      </button>
      <ViewMilestoneModal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        color={color}
        category={categoryLabel}
        description={description}
        organization={organization}
        timePeriod={timePeriod}
      />
      {enableEditing && (
        <Dropdown
          className="goal-card-menu"
          alignRight
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Dropdown.Toggle
            id={`milestone-edit-dropdown-toggle-${id}`}
            className="goal-card-menu-toggle milestone-edit-dropdown-toggle"
          >
            <WingsIcon icon="ellipsis-v" color="var(--w-color-teal-900)" size="mini" />
          </Dropdown.Toggle>
          <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
            <Dropdown.Item onClick={onToggleCompleted}>
              {completed ? (
                <FormattedMessage id="career-plans-mark-incomplete" defaultMessage="Mark Incomplete" />
              ) : (
                <FormattedMessage id="career-plans-mark-complete" defaultMessage="Mark Complete" />
              )}
            </Dropdown.Item>
            <Dropdown.Item onClick={onEdit}>
              <FormattedMessage id="dashboard-item-menu-edit" defaultMessage="Edit" />
            </Dropdown.Item>
            <Dropdown.Item onClick={onDelete}>
              <FormattedMessage id="dashboard-item-menu-delete" defaultMessage="Delete" />
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  )
}

const CareerPlanPanel = ({ title="", description="", milestones=[], mode, color, image, className="", id=null, onEdit=() => null, onMilestoneEdit=(m) => null, onDeleteMilestone=(mId) => null, toggleMilestoneCompletion=(mId)=>null, onSelect=null }) => {
  const planColor = Object.keys(CareerPlanColors).includes(color)
    ? CareerPlanColors[color].color
    : CareerPlanColors.PINK.color
  const displayMode = Object.keys(CareerPlanDisplayModes).includes(mode)
    ? CareerPlanDisplayModes[mode]
    : CareerPlanDisplayModes.VIEW

  return (
    <div className={`guided-career-plan-panel ${className} w-flex w-flex-column w-full w-items-stretch w-gap-1`} id={id}>
      <div className="career-plan-panel-top-container w-flex w-full w-items-center w-gap-2">
        {image && (
          <WingsLink
            className="career-plan-image-link w-rounded w-shrink-0"
            display="block"
            to={`/story/${id}`}
          >
            <img
              src={`${image}?q=&auto=compress,&w=548&h=344&fit=crop&crop=faces`}
              className="career-plan-image w-full"
            />
          </WingsLink>
        )}
        <div className="career-plan-panel-header-button-container w-flex w-items-center w-justify-between w-grow">
          <div className="w-flex w-flex-column w-items-start w-gap-1 w-grow">
            <div className="career-plan-panel-heading-container w-flex w-flex-row w-items-start w-justify-start">
              <WingsHeading level={4} underlineColor={planColor}>
                {title}
              </WingsHeading>
              {displayMode === CareerPlanDisplayModes.EDIT && (
                <WingsButton icon={true} variant="tertiary" size="mini" inline={true} onClick={onEdit}>
                  <WingsIcon icon="pen-outline" size="medium" />
                </WingsButton>
              )}
            </div>
            {image ? (
              <WingsQuoteText size="small" className="w-max-w-65ch">
                {`"${description}"`}
              </WingsQuoteText>
            ) : (
              <WingsText className="w-max-w-65ch">
                {description}
              </WingsText>
            )}          
          </div>
          {displayMode === CareerPlanDisplayModes.TEMPLATE ? (
            <WingsButton
              variant="secondary"
              onClick={onSelect}
              iconName="plus"
              className="w-shrink-0"
              disabled={!onSelect}
              autoLoading={true}
              size="mini"
            >
              <FormattedMessage id="create-a-plan-select-plan" defaultMessage="Select Plan" />
            </WingsButton>
          ) : displayMode === CareerPlanDisplayModes.EDIT ? (
            <WingsButton
              variant="primary"
              size="mini"
              onClick={() => onMilestoneEdit({})}
              className="w-shrink-0 w-mr-2"
            >
              <FormattedMessage id="career-plans-add-milestone" defaultMessage="Add Milestone" />
            </WingsButton>
          ) : (
            null
          )}
        </div>
      </div>
      <div className={`career-plan-milestones-container w-p-1 w-full w-shadow-sm w-flex w-gap-1 w-items-start w-justify-start ${!milestones.length ? "career-plan-empty-milestones-container" : ""}`}>
        {milestones.length > 0 ? (
          <>
            {milestones.map((milestone, idx) => (
              <Milestone
                key={milestone.id || idx}
                id={milestone.id}
                category={milestone.category}
                description={milestone.description}
                organization={milestone.organization}
                enableEditing={displayMode === CareerPlanDisplayModes.EDIT}
                color={planColor}
                completed={milestone.completed}
                startDate={milestone.startDate}
                endDate={milestone.endDate}
                onEdit={() => onMilestoneEdit(milestone)}
                onDelete={() => onDeleteMilestone(milestone.id)}
                onToggleCompleted={() => toggleMilestoneCompletion(milestone.id)}
              />
            ))}
          </>
        ) : displayMode === CareerPlanDisplayModes.EDIT ? (
          <div className="career-plans-no-milestones-message w-flex w-flex-row w-justify-center w-self-center w-grow">
            <WingsButton variant="tertiary" inline={true} size="small" onClick={() => onMilestoneEdit({})}>
              <FormattedMessage id="career-plans-add-a-milestone" defaultMessage="Add a milestone" />
            </WingsButton>
            {" "}
            <WingsText>
              <FormattedMessage id="career-plans-and-start-planning-future" defaultMessage="and start planning your future." />
            </WingsText>
          </div>
        ) : (
          <div className="career-plans-no-milestones-message w-flex w-flex-row w-justify-center w-self-center w-grow">
            <WingsText>
              <FormattedMessage id="career-plans-no-milestones-added-yet" defaultMessage="No milestones have been added to this career plan." />
            </WingsText>
          </div>
        )}
      </div>
    </div>
  )
}

export default CareerPlanPanel
